import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, createSelector } from '@ngxs/store';
import { switchMap, tap } from 'rxjs';
import { ApprovalStatus } from '@shared/modules/approvals/approvals.types';
import { StateBase } from '@state/state-base';
import { ApprovalProcess, ApprovalsStateModel, EligibleApprover } from '../approvals.types';
import { ApprovalsService } from '../approvals.service';
import { Approvals } from './actions';

const initialState: ApprovalsStateModel = {
  approvals: [],
  eligibleApprovers: [],
  loading: false,
  activeApprovalId: null,
};

@State<ApprovalsStateModel>({
  name: new StateToken<ApprovalsStateModel>('approvalsState'),
  defaults: initialState,
})
@Injectable()
export class ApprovalsState extends StateBase {
  constructor(private readonly approvalsService: ApprovalsService) {
    super();
  }

  @Selector()
  static approvals(state: ApprovalsStateModel): ApprovalProcess[] {
    return state.approvals;
  }

  @Selector()
  static loading(state: ApprovalsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static activeApprovalId(state: ApprovalsStateModel): number | null {
    return state.activeApprovalId;
  }

  @Selector()
  static activeApproval(state: ApprovalsStateModel): ApprovalProcess | null {
    return state.approvals.find((a) => a.id === state.activeApprovalId) ?? null;
  }

  @Selector()
  static activeApprovalSnapshot(state: ApprovalsStateModel): Record<string, any> {
    return state.approvals.find((a) => a.id === state.activeApprovalId)?.snapshot ?? {};
  }

  @Selector()
  static eligibleApprovers(state: ApprovalsStateModel): EligibleApprover[] {
    return state.eligibleApprovers;
  }

  static approvalById(id: number) {
    return createSelector(
      [ApprovalsState],
      (state: ApprovalsStateModel) => state.approvals.find((a) => a.id === id) ?? null,
    );
  }

  @Action(Approvals.LoadEligibleApprovers)
  loadEligibleApprovers(ctx: StateContext<ApprovalsStateModel>, { relatedToId }: Approvals.LoadEligibleApprovers) {
    return this.approvalsService
      .getEligibleApprovers(relatedToId)
      .pipe(tap((eligibleApprovers) => ctx.patchState({ eligibleApprovers })));
  }

  @Action(Approvals.LoadApprovals)
  loadApprovals(ctx: StateContext<ApprovalsStateModel>, { relatedToId }: Approvals.LoadApprovals) {
    ctx.patchState({ loading: true });
    return this.approvalsService
      .getApprovals(relatedToId)
      .pipe(tap((approvals) => ctx.patchState({ approvals, loading: false })));
  }

  @Action(Approvals.AddApproval)
  addApproval(ctx: StateContext<ApprovalsStateModel>, { name, layout, relatedToId, snapshot }: Approvals.AddApproval) {
    return this.approvalsService
      .createApproval(name, layout, relatedToId, snapshot)
      .pipe(tap((approval) => ctx.patchState({ approvals: [...ctx.getState().approvals, approval] })));
  }

  @Action(Approvals.DeleteApproval)
  deleteApproval(ctx: StateContext<ApprovalsStateModel>, { id }: Approvals.DeleteApproval) {
    return this.approvalsService
      .deleteApproval(id)
      .pipe(tap(() => ctx.patchState({ approvals: ctx.getState().approvals.filter((a) => a.id !== id) })));
  }

  @Action(Approvals.SetActiveApproval)
  setActiveApproval(ctx: StateContext<ApprovalsStateModel>, { approvalId }: Approvals.SetActiveApproval) {
    ctx.patchState({ activeApprovalId: approvalId });
  }

  @Action(Approvals.UpdateLayout)
  updateLayout(ctx: StateContext<ApprovalsStateModel>, { approvalId, layout }: Approvals.UpdateLayout) {
    return this.approvalsService.updateLayout(approvalId, layout).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.SendApprovalRequest)
  sendApprovalRequest(ctx: StateContext<ApprovalsStateModel>, { approvalId, userIds }: Approvals.SendApprovalRequest) {
    return this.approvalsService.sendApprovalRequest(approvalId, userIds).pipe(
      switchMap(() => this.approvalsService.getApprovalById(approvalId)),
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.UpdateSnapshot)
  updateSnapshot(ctx: StateContext<ApprovalsStateModel>, { approvalId, snapshot }: Approvals.UpdateSnapshot) {
    return this.approvalsService.updateSnapshot(approvalId, snapshot).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.UpdateName)
  updateName(ctx: StateContext<ApprovalsStateModel>, { approvalId, name }: Approvals.UpdateName) {
    return this.approvalsService.updateName(approvalId, name).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === approvalId ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.RefreshApproval)
  refreshApproval(ctx: StateContext<ApprovalsStateModel>, { id }: Approvals.RefreshApproval) {
    return this.approvalsService.getApprovalById(id).pipe(
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === id ? updated : a)),
        }),
      ),
    );
  }

  @Action(Approvals.Reset)
  reset(ctx: StateContext<ApprovalsStateModel>) {
    ctx.setState(initialState);
  }

  @Action(Approvals.SubmitReview)
  submitReview(ctx: StateContext<ApprovalsStateModel>, { approvalRowId, status, comments }: Approvals.SubmitReview) {
    const activeApprovalId = ctx.getState().activeApprovalId;
    const request$ =
      status === ApprovalStatus.APPROVED
        ? this.approvalsService.approveReview(approvalRowId, comments)
        : this.approvalsService.declineReview(approvalRowId, comments);

    if (!activeApprovalId) return request$;

    return request$.pipe(
      switchMap(() => this.approvalsService.getApprovalById(activeApprovalId)),
      tap((updated) =>
        ctx.patchState({
          approvals: ctx.getState().approvals.map((a) => (a.id === activeApprovalId ? updated : a)),
        }),
      ),
    );
  }
}
